import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Home as HomeIcon, PenTool, MapPin, Hammer } from "lucide-react";
import Seo from "../seo/Seo";
import { Section, Heading, GoldButton, CTASection, FAQ, faqSchema } from "../components/ui-kit";
import { IMAGES } from "../mock/mock";
import { CTA, FAQS, PROCESS_STEPS, FEATURED_HOMES, REVIEWS, COUNTIES, BUSINESS } from "../data/site";

const pillars = [
  { icon: PenTool, title: "Custom Design", text: "Every home is designed around your lifestyle, your taste, and your specific lot." },
  { icon: MapPin, title: "Build on Your Lot", text: "We build on your land across Central Florida, tailored to your site." },
  { icon: Hammer, title: "True Craftsmanship", text: "Quality construction and finish work that stands the test of time." },
  { icon: HomeIcon, title: "Local Expertise", text: "Deep knowledge of building across Brevard, Orange, Osceola, Seminole & Volusia." },
];

const Home = () => (
  <>
    <Seo
      title="Custom Home Builder in Central Florida | Build Highland Homes"
      description="Build Highland Homes is a custom home builder serving Central Florida. We design and build custom homes on your lot across Brevard, Orange, Osceola, Seminole & Volusia counties."
      path="/"
      keywords="custom home builder, custom homes, custom home builder central florida, build on your lot, custom home design"
      schema={faqSchema(FAQS)}
    />

    {/* Hero */}
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.hero} alt="Custom home built by Build Highland Homes in Central Florida" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0b]/80 via-[#0b0b0b]/55 to-[#0b0b0b]" />
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-28">
        <div className="flex items-center justify-center gap-4 mb-6">
          <span className="w-12 h-px bg-[#c9a24b]" />
          <span className="text-[#e3c77a] tracking-[0.32em] uppercase text-xs md:text-sm">Custom Home Builder · Central Florida</span>
          <span className="w-12 h-px bg-[#c9a24b]" />
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl font-semibold leading-[1.05] text-[#f3ede0] mb-7">
          Build a Custom Home,<br /><span className="gold-gradient-text">Built Around You</span>
        </h1>
        <p className="max-w-2xl mx-auto text-[#d8d0c0] text-base md:text-lg leading-relaxed mb-10">
          Build Highland Homes designs and builds one-of-a-kind custom homes on your lot across Central Florida. From your first idea to the final walkthrough, we craft a home that's truly yours.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <GoldButton to={CTA.primary.to}>{CTA.primary.label}</GoldButton>
          <GoldButton to="/custom-homes" variant="outline">Explore Custom Homes</GoldButton>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#c9a24b] animate-bounce"><ChevronDown size={30} /></div>
    </section>

    {/* Positioning */}
    <Section className="bg-[#0b0b0b]">
      <Heading overline="Who We Are" title="A Custom Home Builder Serving Central Florida" subtitle="We are not a production or tract builder. Build Highland Homes designs and builds custom homes for homeowners — every one built on your lot and tailored to you." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {pillars.map((p) => (
          <div key={p.title} className="bg-[#141414] border border-[#c9a24b]/15 p-7 hover:border-[#c9a24b]/40 transition-colors">
            <p.icon className="text-[#c9a24b] mb-4" size={30} />
            <h3 className="font-serif-display text-xl text-[#f3ede0] mb-2">{p.title}</h3>
            <p className="text-sm text-[#a39d90] leading-relaxed">{p.text}</p>
          </div>
        ))}
      </div>
    </Section>

    {/* Featured Homes */}
    <Section className="bg-[#0f0f0f]">
      <Heading overline="Our Work" title="Featured Custom Homes" subtitle="A look at custom homes and craftsmanship from Build Highland Homes across Central Florida." />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {FEATURED_HOMES.slice(0, 6).map((h, i) => (
          <div key={i} className="relative group overflow-hidden">
            <img src={IMAGES[h.image]} alt={`${h.title} — custom home by Build Highland Homes`} loading="lazy" className="w-full h-52 md:h-64 object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent opacity-80" />
            <div className="absolute bottom-0 left-0 p-4">
              <span className="text-[#e3c77a] text-[10px] tracking-[0.2em] uppercase">{h.category}</span>
              <h3 className="font-serif-display text-lg text-[#f3ede0]">{h.title}</h3>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mt-10"><GoldButton to="/projects" variant="outline">View All Featured Homes</GoldButton></div>
    </Section>

    {/* Process preview */}
    <Section className="bg-[#0b0b0b]">
      <Heading overline="How We Build" title="The Highland Custom Home Process" subtitle="A clear, transparent path from first conversation to the keys of your finished custom home." />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
        {PROCESS_STEPS.slice(0, 5).map((s) => (
          <div key={s.n} className="text-center">
            <div className="mx-auto w-14 h-14 mb-4 flex items-center justify-center rounded-full border border-[#c9a24b]/40"><span className="font-serif-display text-xl text-[#e3c77a]">{s.n}</span></div>
            <h3 className="font-serif-display text-lg text-[#f3ede0] mb-2">{s.title}</h3>
            <p className="text-xs text-[#a39d90] leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
      <div className="text-center mt-10"><GoldButton to="/our-process" variant="outline">See the Full Process</GoldButton></div>
    </Section>

    {/* Locations */}
    <Section className="bg-[#0f0f0f]">
      <Heading overline="Where We Build" title="Serving All of Central Florida" subtitle="Build Highland Homes builds custom homes across five Central Florida counties." />
      <div className="flex flex-wrap justify-center gap-4">
        {COUNTIES.map((c) => (
          <Link key={c.slug} to={`/locations/${c.slug}`} className="border border-[#c9a24b]/30 hover:bg-[#c9a24b] hover:text-[#0b0b0b] text-[#e3c77a] px-6 py-3 transition-colors font-serif-display text-lg">{c.name}</Link>
        ))}
      </div>
    </Section>

    {/* Reviews */}
    <Section className="bg-[#0b0b0b]">
      <Heading overline="Homeowner Reviews" title="What Our Clients Say" />
      <div className="grid md:grid-cols-3 gap-6">
        {REVIEWS.map((r) => (
          <div key={r.name} className="bg-[#141414] border border-[#c9a24b]/15 p-7">
            <p className="font-serif-display text-lg text-[#e8e1d2] italic leading-relaxed mb-5">“{r.text}”</p>
            <p className="text-[#e3c77a] font-medium">{r.name}</p>
            <p className="text-[#a39d90] text-sm">{r.project}</p>
          </div>
        ))}
      </div>
    </Section>

    {/* FAQ */}
    <Section className="bg-[#0f0f0f]">
      <Heading overline="Questions" title="Custom Home Building FAQs" />
      <FAQ items={FAQS} />
    </Section>

    <CTASection />
  </>
);

export default Home;
